/**
 * Screen 1 — Pending Payment Dashboard.
 *
 * Every tile, bar and funnel row is a link into the register with that filter
 * already applied; every figure carries its definition and its denominator.
 */
import { useCallback, useEffect, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, Loader2, Ban, Clock, FileWarning, Gavel, Landmark, Wallet } from 'lucide-react';
import api from '@/lib/api';
import { STAGE_LABEL, money, num, int, toneColor, useIptPrefs, useIptTheme } from './iptTheme';
import { Bar, Def, Empty, Panel, Sample, StageChip, Tile } from './IptPrimitives';

const ICON = {
  pending: Wallet, overdue: Clock, blocked: Ban, retention: Landmark,
  disputed: Gavel, oldest: FileWarning,
};

const BUCKET_TONE = ['primary', 'amber', 'accent', 'red'];

export const IptDashboard = ({ projectId, vimSource, go }) => {
  const T = useIptTheme();
  const prefs = useIptPrefs();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setErr(null);
    api.get(`/ipt/metrics/dashboard?project_id=${projectId || ''}&${prefs.query}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not load the dashboard'));
  }, [projectId, prefs.query]);
  useEffect(load, [load]);

  if (err) return <Empty T={T} testId="ipt-dashboard-error">{String(err)}</Empty>;
  if (!d) {
    return (
      <div className="py-20 text-center" data-testid="ipt-dashboard-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: T.primary }} />
      </div>
    );
  }

  const t = d.tiles || {};
  const tb = d.tileBasis || {};
  const cur = t.currency || 'INR';
  const basis = d.basis;
  const defs = d.definitions || {};
  const fc = d.forecast || {};

  if (!d.sample?.invoices) {
    return (
      <Panel T={T} testId="ipt-dashboard-empty" title="No invoices on this project"
        subtitle="Nothing to show — this is an empty register, not a failed load">
        <p className="text-[11.5px] leading-relaxed max-w-[620px]" style={{ color: T.body }}>
          The invoice module holds no records for the currently selected project. Switch
          project in the header, or import from Contracts &amp; Claims. Every figure on this
          screen is derived from invoices and their stage transitions, so an empty project
          shows zeros rather than a blank page.
        </p>
      </Panel>
    );
  }

  const tileDef = (key, title) => ({
    title, body: tb[key]?.statement, basis,
  });

  return (
    <div className="space-y-4" data-testid="ipt-dashboard">
      {/* ── value tiles ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Tile T={T} testId="ipt-tile-pending" label="Pending payment"
          value={money(t.pendingValue, cur)}
          sub={`${int(t.pendingCount)} invoices unpaid`}
          def={tileDef('pendingValue', 'Pending payment')}
          onClick={() => go({ tab: 'register' })}
          extra={<Row T={T}><ICON.pending className="w-3 h-3" /> all open liabilities</Row>} />

        <Tile T={T} testId="ipt-tile-overdue" label="Overdue" tone={T.red}
          value={money(t.overdueValue, cur)}
          sub={`${int(t.overdueCount)} past the due date`}
          def={tileDef('overdueValue', 'Overdue')}
          onClick={() => go({ tab: 'register', overdue: 'true' })}
          extra={(
            <div className="mt-1.5 pt-1.5 space-y-0.5" style={{ borderTop: `1px dashed ${T.border}` }}>
              <Row T={T}>{int(t.overdueNativeCount)} with full stage history</Row>
              <Row T={T} tone={T.amber}>
                {int(t.overdueImportedCount)} imported — stage as at import date
              </Row>
            </div>
          )} />

        <Tile T={T} testId="ipt-tile-blocked" label="Blocked" tone={T.amber}
          value={money(t.blockedValue, cur)}
          sub={`${int(t.blockedCount)} carrying a block`}
          def={tileDef('blockedValue', 'Blocked')}
          onClick={() => go({ tab: 'register', blocked: 'true' })}
          extra={<Row T={T}><ICON.blocked className="w-3 h-3" /> will not pay until cleared</Row>} />

        <Tile T={T} testId="ipt-tile-oldest" label="Oldest open invoice"
          value={`${num(t.oldestInvoiceDays, 0)} d`}
          sub={`receipt to today · ${basis}`}
          def={tileDef('oldestInvoiceDays', 'Oldest open invoice')}
          onClick={() => go({ tab: 'register', sort: 'receipt_date', direction: '1' })} />

        <Tile T={T} testId="ipt-tile-retention" label="Retention held"
          value={money(t.retentionHeld, cur)}
          sub={t.retentionOverdueCount
            ? `${int(t.retentionOverdueCount)} past release milestone`
            : 'all within milestone'}
          tone={t.retentionOverdueCount ? T.accent : undefined}
          def={tileDef('retentionHeld', 'Retention held')}
          onClick={() => go({ tab: 'retention' })} />

        <Tile T={T} testId="ipt-tile-disputed" label="Disputed value" tone={T.red}
          value={money(t.disputedValue, cur)}
          sub="currently in dispute"
          def={{ title: 'Disputed value',
            body: 'Net payable on invoices whose current stage is Disputed. Each '
                + 'dispute carries a declared party — who must act next — and a '
                + 'stated basis. Attribution is a record of fact, not an inference '
                + 'from the stage.',
            axis: 'attribution', version: defs.delay_attribution?.version }}
          onClick={() => go({ tab: 'register', disputed: 'true' })} />
      </div>

      {d.sample?.statement && <Sample T={T} text={d.sample.statement} testId="ipt-dashboard-sample" />}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* ── ageing ───────────────────────────────────────────────── */}
        <Panel T={T} testId="ipt-ageing" title="Ageing of unpaid invoices"
          subtitle={`${(defs.ageing || {}).basis || ''} · ${basis}`}
          right={<Def T={T} testId="ipt-ageing-def" title="Ageing buckets"
            body={(defs.ageing || {}).basis}
            basis={basis}
            sample={`Buckets at ${((defs.ageing || {}).buckets || []).join(', ')} days. Paid and rejected invoices are excluded.`} />}>
          <div className="space-y-2">
            {(d.ageing || []).map((b, i) => {
              const c = toneColor(T, BUCKET_TONE[i] || 'muted');
              const maxV = Math.max(...(d.ageing || []).map((x) => x.value || 0), 1);
              return (
                <button key={b.bucket} type="button"
                  data-testid={`ipt-ageing-${b.bucket}`}
                  onClick={() => go({ tab: 'register', ageing_bucket: b.bucket })}
                  className="w-full text-left group">
                  <div className="flex items-baseline gap-2 text-[11px]">
                    <span className="w-14 font-semibold" style={{ color: T.ink }}>{b.bucket} d</span>
                    <span className="tabular-nums" style={{ color: T.body }}>{int(b.count)}</span>
                    <span className="ml-auto tabular-nums font-semibold" style={{ color: c }}>
                      {money(b.value, cur)}
                    </span>
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100"
                      style={{ color: T.primary }} />
                  </div>
                  <div className="h-2 mt-1 rounded-full overflow-hidden"
                    style={{ background: T.surfaceAlt }}>
                    <div style={{ width: `${(100 * (b.value || 0)) / maxV}%`, height: '100%',
                      background: c }} />
                  </div>
                </button>
              );
            })}
            {!(d.ageing || []).length && <Empty T={T}>No unpaid invoices.</Empty>}
          </div>
        </Panel>

        {/* ── cash-out ─────────────────────────────────────────────── */}
        <Panel T={T} testId="ipt-forecast" title="Cash-out obligation"
          subtitle={fc.label}
          right={(
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wide"
                data-testid="ipt-forecast-classification"
                style={{ background: T.tint(T.primary, '1F'), color: T.primary,
                         border: `1px solid ${T.tint(T.primary, '44')}` }}>
                {fc.classification || 'contractual'}
              </span>
              <Def T={T} testId="ipt-forecast-def" title="Cash-out obligation"
                body={fc.method} basis={basis} sample={fc.notAForecastBecause} />
            </div>
          )}>
          <div className="space-y-2.5">
            {(fc.windows || []).map((w) => (
              <button key={w.weeks} type="button" data-testid={`ipt-forecast-w${w.weeks}`}
                onClick={() => go({ tab: 'register', sort: 'due_date', direction: '1' })}
                className="w-full text-left group">
                <div className="flex items-baseline gap-2 text-[11px]">
                  <span className="w-20 font-semibold" style={{ color: T.ink }}>
                    next {w.weeks} wks
                  </span>
                  <span className="tabular-nums" style={{ color: T.body }}>{int(w.count)} inv</span>
                  <span className="ml-auto tabular-nums font-semibold" style={{ color: T.ink }}>
                    {money(w.value, cur)}
                  </span>
                </div>
                <Bar T={T} testId={`ipt-forecast-bar-w${w.weeks}`} segments={[
                  { label: 'treasury-scheduled', value: w.scheduledValue, colour: T.primary },
                  { label: 'contractual due date', value: w.dueDateValue, colour: T.primaryLight },
                ]} />
                <div className="flex gap-3 text-[9.5px] mt-1" style={{ color: T.muted }}>
                  <span><Dot c={T.primary} /> scheduled {money(w.scheduledValue, cur)}</span>
                  <span><Dot c={T.primaryLight} /> due date {money(w.dueDateValue, cur)}</span>
                  {w.blockedValue > 0 && (
                    <span style={{ color: T.red }}>
                      <Dot c={T.red} /> of which blocked {money(w.blockedValue, cur)}
                      {' '}({int(w.blockedCount)})
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
          <Sample T={T} testId="ipt-forecast-sample"
            text={`Dated from ${(fc.basis || {}).preferred}, otherwise ${(fc.basis || {}).fallback}. `
              + `${(fc.basis || {}).blocked}. Excludes ${(fc.basis || {}).excluded}.`} />
          {(fc.schedule || []).length > 1 && (
            <div className="h-28 mt-3">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={80} debounce={60}>
                <AreaChart data={cumulative(fc.schedule)}>
                  <defs>
                    <linearGradient id="iptCash" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.primary} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={T.primary} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={T.border} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: T.muted }} />
                  <YAxis tick={{ fontSize: 9, fill: T.muted }}
                    tickFormatter={(v) => money(v, cur)} width={62} />
                  <Tooltip formatter={(v) => money(v, cur)}
                    contentStyle={{ background: T.surface, border: `1px solid ${T.border}`,
                      fontSize: 11, borderRadius: 6 }} />
                  <Area type="monotone" dataKey="cum" stroke={T.primary} strokeWidth={2}
                    fill="url(#iptCash)" name="cumulative obligation" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      {/* ── lifecycle funnel ───────────────────────────────────────── */}
      <Panel T={T} testId="ipt-funnel" title="Lifecycle position"
        subtitle={d.bottleneck
          ? `Slowest pre-payment stage: ${STAGE_LABEL[d.bottleneck.stage] || d.bottleneck.stage} — `
            + `${num(d.bottleneck.avgDwellDays)} ${basis} average dwell`
          : 'Where the money is sitting'}
        right={<Def T={T} testId="ipt-funnel-def" title="Average dwell"
          body={"Mean time an invoice has spent in that stage, summed from the transition table. The per-stage figures add up to the gross cycle time exactly, because every period belongs to exactly one stage."}
          basis={basis}
          sample="Dwell excludes invoices imported without stage history — a single synthetic transition is not a duration." />}>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="ipt-funnel-table">
            <thead>
              <tr style={{ color: T.muted }}>
                <th className="text-left font-semibold py-1.5 pr-3">Stage</th>
                <th className="text-right font-semibold py-1.5 px-2">Invoices</th>
                <th className="text-right font-semibold py-1.5 px-2">Value</th>
                <th className="text-right font-semibold py-1.5 px-2">Avg dwell</th>
                <th className="text-left font-semibold py-1.5 pl-2 w-[32%]">Share of open value</th>
              </tr>
            </thead>
            <tbody>
              {(d.funnel || []).filter((f) => f.count > 0).map((f) => {
                const maxV = Math.max(...(d.funnel || []).map((x) => x.value || 0), 1);
                return (
                  <tr key={f.stage} data-testid={`ipt-funnel-${f.stage}`}
                    onClick={() => go({ tab: 'register', stage: f.stage })}
                    className="cursor-pointer hover:opacity-80"
                    style={{ borderTop: `1px solid ${T.border}` }}>
                    <td className="py-1.5 pr-3"><StageChip stage={f.stage} T={T} /></td>
                    <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.ink }}>
                      {int(f.count)}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                      style={{ color: T.ink }}>{money(f.value, cur)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.body }}>
                      {f.avgDwellDays === null ? '—' : `${num(f.avgDwellDays)} d`}
                      {f.samples ? (
                        <span className="text-[9px] ml-1" style={{ color: T.muted }}>
                          n={f.samples}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-1.5 pl-2">
                      <div className="h-1.5 rounded-full overflow-hidden"
                        style={{ background: T.surfaceAlt }}>
                        <div style={{ width: `${(100 * (f.value || 0)) / maxV}%`, height: '100%',
                          background: T.primary }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* ── most overdue ───────────────────────────────────────────── */}
      <Panel T={T} testId="ipt-overdue-list" title="Furthest beyond terms"
        subtitle={`${int((d.overdue || []).length)} invoices, worst first`}
        right={<Def T={T} testId="ipt-overdue-def" title="Days beyond terms"
          body={(defs.terms || {}).basis} basis={basis}
          sample="Imported records are included and marked — their due date is an input, but their stage is only known as at the import date." />}>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="ipt-overdue-table">
            <thead>
              <tr style={{ color: T.muted }}>
                {['Invoice', 'Vendor', 'Stage', 'Due', 'Beyond terms', 'Net payable']
                  .map((h, i) => (
                    <th key={h} className={`py-1.5 font-semibold ${i > 2 ? 'text-right px-2' : 'text-left pr-3'}`}>
                      {h}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {(d.overdue || []).slice(0, 12).map((r) => (
                <tr key={r.invoice_id} data-testid={`ipt-overdue-row-${r.invoice_no}`}
                  onClick={() => go({ invoice: r.invoice_id })}
                  className="cursor-pointer hover:opacity-80"
                  style={{ borderTop: `1px solid ${T.border}` }}>
                  <td className="py-1.5 pr-3 font-semibold" style={{ color: T.primary }}>
                    {r.invoice_no}
                    {r.is_blocked && (
                      <span className="ml-1.5 px-1 rounded text-[8.5px] font-bold uppercase"
                        style={{ background: T.tint(T.red, '1F'), color: T.red }}>blocked</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 truncate max-w-[180px]" style={{ color: T.body }}>
                    {r.vendor_name}
                  </td>
                  <td className="py-1.5 pr-3"><StageChip stage={r.current_stage} T={T} /></td>
                  <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.body }}>
                    {(r.due_date || '').slice(0, 10)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                    style={{ color: T.red }}>{num(r.daysBeyondTerms, 0)} d</td>
                  <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.ink }}>
                    {money(r.net_payable, r.currency || cur)}
                  </td>
                </tr>
              ))}
              {!(d.overdue || []).length && (
                <tr><td colSpan={6}><Empty T={T}>Nothing is beyond terms.</Empty></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};

const Row = ({ children, T, tone }) => (
  <div className="flex items-center gap-1 text-[9.5px] mt-1" style={{ color: tone || T.muted }}>
    {children}
  </div>
);

const Dot = ({ c }) => (
  <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
    style={{ background: c }} />
);

function cumulative(schedule) {
  const byWeek = {};
  schedule.forEach((s) => {
    const k = Math.max(0, s.week || 0);
    byWeek[k] = (byWeek[k] || 0) + (s.amount || 0);
  });
  let cum = 0;
  return Object.keys(byWeek).map(Number).sort((a, b) => a - b).slice(0, 16).map((w) => {
    cum += byWeek[w];
    return { label: w === 0 ? 'now' : `w${w}`, cum: Math.round(cum) };
  });
}

export default IptDashboard;
